import React, { Component } from 'react';
import { Field, FieldArray, reduxForm } from 'redux-form';
import { isMoneyAmount, ModuleType, ModuleTypeICSConfig, validatePrice } from 'src/common/index';
import {
    renderCheckbox,
    renderInput,
    renderMultiLanguageDocumentUpload,
    renderSelectField,
} from 'src/components/misc/redux-form-helpers';
import MailConfigForm, { validateMailServerConfig } from 'src/components/superadmin/modules/mail-config-form';
import { getMenuItemsByEnum, getMenuItemsModuleConfig } from 'src/utils/helpers';
import autoBind from 'react-autobind';
import { isEmpty } from 'lodash';

class ModuleEditForm extends Component {
    constructor(props) {
        super(props);
        autoBind(this);

        this.state = {
            selectedModuleType: undefined,
        };
    }

    render() {
        const { handleSubmit, modules, isCreate, t, fetchedModuleTypes, storeModule, upload, initialValues } =
            this.props;
        const moduleTypeMenuItems = getMenuItemsByEnum(
            Object.keys(ModuleType).sort((a, b) => a.localeCompare(b)),
            modules.map(module => module.type),
        );

        const hasOneTimeFee =
            (this.state.selectedModuleType !== undefined && this.state.selectedModuleType.oneTimeFee) ||
            initialValues.oneTimeFee;
        return (
            <form className="form-horizontal" onSubmit={handleSubmit}>
                <div className="row">
                    <div className="col-md-6">
                        <Field
                            name="type"
                            label={`${t('common_phrases.type')} *`}
                            component={renderSelectField}
                            disabled={!isCreate}
                            onChange={this.renderModuleTypeInfo}>
                            {moduleTypeMenuItems}
                        </Field>
                    </div>
                    {storeModule && (
                        <div className="col-md-6 voffset40">
                            <Field
                                name="featureInStore"
                                label={t('modules_store.feature_in_store')}
                                component={renderCheckbox}
                            />
                        </div>
                    )}
                    {!storeModule && (
                        <div className="col-md-6 voffset40">
                            <Field name="active" label={t('common_phrases.active')} component={renderCheckbox} />
                        </div>
                    )}
                </div>
                <div className="row">
                    <div className="col-md-6">
                        <Field name="fee.amount" label={`${t('booked_tasks.fee')} *`} component={renderInput} />
                    </div>
                    {hasOneTimeFee && (
                        <div className="col-md-6">
                            <Field
                                name="oneTimeFeeAmount.amount"
                                label={`${t('modules_store.one_time_fee_form')} *`}
                                component={renderInput}
                            />
                        </div>
                    )}
                </div>

                {storeModule && (
                    <div className="row">
                        <div className="col-md-12">
                            <div className="row">
                                <FieldArray
                                    name="infoDocuments"
                                    component={this.renderDocuments}
                                    props={{ t, upload }}
                                />
                            </div>
                        </div>
                    </div>
                )}
                {!storeModule && this.renderModuleConfigByType()}
                {storeModule && this.renderStoreModuleConfigByTpe()}
            </form>
        );
    }

    renderDocuments = ({ fields, t, upload }) => {
        return (
            <div className="col-md-12">
                <h3>{t('modules_store.documents.add_documents')}</h3>
                {fields.map((member, index) => (
                    <Field
                        key={index}
                        name={member} // 'member' is the path: "infoDocuments[0]", etc.
                        placeholder={t('modules_stores.documents.document')}
                        label={`${t('modules_stores.documents.document')} ${index + 1}`}
                        t={t}
                        component={renderMultiLanguageDocumentUpload}
                        upload={upload}
                    />
                ))}

                <button
                    type="button"
                    className="btn btn-success"
                    onClick={() => fields.push(null)}
                    style={{ marginTop: '10px' }}>
                    <i className="fa fa-plus" /> {t('modules_store.documents.add_documents')}
                </button>
            </div>
        );
    };

    renderStoreModuleConfigByTpe() {
        const { module, isCreate, t } = this.props;

        const moduleType = !isCreate && module != null ? module.type : this.props.selectedType;
        switch (moduleType) {
            case ModuleType.EMAIL_TO_OFFER:
                return (
                    <div className="row">
                        <div className="col-md-6">
                            <Field name="transactionFee.amount" label={t('transaction_fee')} component={renderInput} />
                        </div>
                    </div>
                );
            default:
                return <div />;
        }
    }

    renderModuleTypeInfo(event, value) {
        this.setState({
            selectedModuleType: this.props.fetchedModuleTypes.find(moduleType => moduleType.type === value),
        });
    }

    /**
     * Render module config input fields depending on module type
     */
    renderModuleConfigByType() {
        const { module, isCreate, t } = this.props;
        const icsModuleTypeConfig = getMenuItemsModuleConfig(Object.keys(ModuleTypeICSConfig));

        const moduleType = !isCreate && module != null ? module.type : this.props.selectedType;
        switch (moduleType) {
            case ModuleType.CUSTOM_MAIL_SERVER:
                return <MailConfigForm config={module != null ? module.config : null} t={t} />;
            case ModuleType.ONLINE_PAYMENT:
                return (
                    <div className="row">
                        <div className="col-md-6">
                            <Field name="transactionFee.amount" label={t('transaction_fee')} component={renderInput} />
                        </div>
                    </div>
                );
            case ModuleType.EMAIL_TO_OFFER:
                return (
                    <div className="row">
                        <div className="col-md-6">
                            <Field name="transactionFee.amount" label={t('transaction_fee')} component={renderInput} />
                        </div>
                    </div>
                );
            case ModuleType.ICS_FILE_BOOKING_CONFIRMATION:
                return (
                    <Field
                        name="config.ics_file_config"
                        label={`${t('common_phrases.type')} *`}
                        component={renderSelectField}
                        value={module != null && module.config != null ? module.config.ics_file : null}>
                        {icsModuleTypeConfig}
                    </Field>
                );
            default:
                return <div />;
        }
    }
}

/**
 * Validates the module form
 * In addition, depending on the module type, the related config gets also validated
 */
const validate = (values, props) => {
    const { t, isCreate } = props;
    const errors = {
        fee: {
            amount: undefined,
        },
        transactionFee: {
            amount: undefined,
        },
    };

    if (isCreate && values.type == null) errors.type = t('error_missing.fill_in');

    if (!isMoneyAmount(values.fee)) errors.fee.amount = t('error_missing.fill_in_fee');
    else if (validatePrice(values.fee.amount))
        errors.fee.amount = `${t('error_hint.error_invalid_format')} (######.00)`;
    else if (values.fee.amount < 0 || values.fee.amount > 1000) errors.fee.amount = t('error_hint.fee_criteria');

    if (isMoneyAmount(values.transactionFee)) {
        if (validatePrice(values.transactionFee.amount))
            errors.transactionFee.amount = `${t('error_hint.error_invalid_format')} (######.00)`;
        else if (values.transactionFee.amount < 0 || values.transactionFee.amount > 1000)
            errors.transactionFee.amount = t('error_hint.fee_criteria');
    }

    // validate config depending on module type
    switch (values.type) {
        case ModuleType.CUSTOM_MAIL_SERVER:
            errors.config = validateMailServerConfig(values.config, t);
            break;
        case ModuleType.ICS_FILE_BOOKING_CONFIRMATION:
            if (isEmpty(values.config)) errors.config = t('error_hint.fee_criteria');
            break;
        default:
            errors.config = undefined;
            break;
    }

    return errors;
};

export default reduxForm({
    form: 'moduleEditForm',
    validate,
})(ModuleEditForm);
